import { supabase } from './supabase';

type Team = 'h' | 'p';
type Mode = 'd' | 'l';

interface JoeAIApi {
  help(): string;
  whoami(): string;
  projects(): Promise<string>;
  articles(): Promise<string>;
  open(slug: string): Promise<string>;
  theme(team: Team, mode: Mode): string;
  stats(): Promise<string>;
  contact(): { email: string; linkedin: string; form: string };
}

declare global {
  interface Window {
    joeAI: JoeAIApi;
  }
}

const banner = `
%c hi.
%c i'm joe. you opened the console — figured i'd say hi.
%c try: joeAI.help()
`;

// Fires once on import.
console.log(
  banner,
  'color:#00788C;font:bold 14px monospace',
  'color:#9AA0A6;font:13px monospace',
  'color:#2EB1C9;font:12px monospace'
);

const joeAI: JoeAIApi = {
  help() {
    console.log('%cjoeAI · console api', 'color:#00788C;font:bold 13px monospace');
    console.table({
      'joeAI.help()':     'this',
      'joeAI.whoami()':   'who joe is',
      'joeAI.projects()': 'live projects (queries supabase)',
      'joeAI.articles()': 'published articles (queries supabase)',
      'joeAI.open(slug)': 'opens project or article',
      'joeAI.theme(t,m)': "theme('h'|'p', 'd'|'l')",
      'joeAI.stats()':    'visits, chats, projects last 7d',
      'joeAI.contact()':  'email + linkedin',
    });
    return '↑';
  },

  whoami() {
    return `joe hamilton — ai systems architect, charlotte nc.
co-founder + cto, servet digital.
builds in: typescript, react, supabase, claude, gpt, gemini, perplexity.`;
  },

  async projects() {
    const { data, error } = await supabase
      .from('projects')
      .select('slug, name, status, tagline')
      .neq('status', 'private')
      .order('year_completed', { ascending: false, nullsFirst: false });
    if (error) return `// supabase: ${error.message}`;
    console.table(data);
    return `${data?.length ?? 0} projects`;
  },

  async articles() {
    const { data, error } = await supabase
      .from('articles')
      .select('slug, title, publish_date')
      .eq('status', 'published')
      .order('publish_date', { ascending: false });
    if (error) return `// supabase: ${error.message}`;
    console.table(data);
    return `${data?.length ?? 0} articles`;
  },

  async open(slug) {
    if (!slug) return 'usage: joeAI.open("the-workaround")';

    const { data: project, error: projErr } = await supabase
      .from('projects')
      .select('slug, name')
      .eq('slug', slug)
      .neq('status', 'private')
      .maybeSingle();
    if (projErr) return `// supabase: ${projErr.message}`;
    if (project) {
      const p = project as { slug: string; name: string };
      window.location.assign(`/work/${p.slug}`);
      return `opening project: ${p.name}`;
    }

    const { data: article, error: artErr } = await supabase
      .from('articles')
      .select('slug, title')
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle();
    if (artErr) return `// supabase: ${artErr.message}`;
    if (article) {
      const a = article as { slug: string; title: string };
      window.location.assign(`/articles/${a.slug}`);
      return `opening article: ${a.title}`;
    }

    return `not found: ${slug}`;
  },

  theme(team, mode) {
    const t = team === 'h' ? 'hornets' : 'panthers';
    const m = mode === 'd' ? 'dark' : 'light';
    // Writes the same localStorage keys and attrs ThemeContext reads, so the
    // DOM flips immediately and persists across reload. React state doesn't
    // rerun until remount but the paint is correct.
    document.documentElement.setAttribute('data-team', t);
    document.documentElement.setAttribute('data-mode', m);
    localStorage.setItem('theme-team', t);
    localStorage.setItem('theme-mode', m);
    return `ok · ${t} · ${m}`;
  },

  async stats() {
    // Routes through get_public_site_stats RPC rather than direct reads on
    // chat_sessions + site_analytics, which are RLS-locked for anon.
    const { data, error } = await supabase.rpc('get_public_site_stats');
    if (error) return `// supabase: ${error.message}`;
    const s = data as {
      visitors_7d: number;
      chats_7d: number;
      projects_live: number;
    };
    return `${s.visitors_7d} visits · ${s.chats_7d} chats · ${s.projects_live} projects live · last 7d`;
  },

  contact() {
    return {
      email: 'j.hamilton@servetdigital.com',
      linkedin: 'in/joehamiltondev',
      form: '/contact',
    };
  },
};

window.joeAI = joeAI;
